import React from "react";

import { parse } from "querystring";

import { Box, List, ListItem, Typography } from "@material-ui/core";
import { DateTime } from "luxon";
import { useLocation, useParams } from "react-router";

import OrderQuery from "api/queries/OrderQuery";
import Address from "api/types/Address";
import { AvailableOrderTypes } from "api/types/enums";
import BusinessHeader from "components/BusinessHeader";
import Loading from "components/Loading";
import OrderItemListItem from "components/OrderItemListItem";
import Price from "components/Price";
import makeStyles from "hooks/makeStyles";
import useFormatAddress from "hooks/useFormatAddress";
import useQuery from "hooks/useQuery";
import useTranslation from "hooks/useTranslation";
import ReceiptIcon from "icons/ReceiptIcon";
import UserIcon from "icons/UserIcon";

const fontSize = "14px";
const lineHeight = "16px";

const useStyles = makeStyles(theme => ({
	root: {
		margin: theme.spacing(2),
		[theme.breakpoints.up(480)]: {
			maxWidth: "375px",
			margin: "0 auto",
			padding: "0 8px"
		}
	},
	subheader: {
		marginTop: "19px",
		fontSize: "16px",
		lineHeight: "19px",
		fontWeight: "bold",
		textTransform: "uppercase"
	},
	orderStatus: {
		marginTop: "11px",
		borderTop: "1px solid #bdbdbd",
		borderBottom: "1px solid #bdbdbd",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: "16px"
	},
	orderStatusIcon: {
		marginTop: "2px"
	},
	orderStatusText: {
		fontSize: "28px",
		lineHeight: "34px",
		textAlign: "center"
	},
	orderStatusStatusText: {
		fontSize: "12px",
		lineHeight: "14px",
		textAlign: "center"
	},
	orderInfo: {
		padding: "0 0 16px 0"
	},
	orderInfoPersonalInformationHeader: {
		display: "flex",
		alignItems: "center",
		fontWeight: 500,
		margin: "16px 0",
		lineHeight: "14px",
		textTransform: "uppercase",

		"& svg": {
			marginRight: "9px"
		}
	},
	orderInfoDeliveryInfo: {
		fontSize: "12px",
		lineHeight: "14px",
		textTransform: "uppercase"
	},
	items: {
		listStyle: "none",
		paddingTop: 0
	},
	itemsHeader: {
		textTransform: "uppercase",
		fontWeight: 500,

		"& svg": {
			marginRight: "9px"
		}
	},
	item: {
		marginTop: theme.spacing(1)
	},
	totals: {
		marginTop: theme.spacing(1),
		listStyle: "none",
		borderTop: "1px solid #bdbdbd"
	},
	totalsItem: {
		margin: 0,
		padding: `${theme.spacing(1)}px ${theme.spacing(1)}px 0 ${theme.spacing(1)}px`,
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between"
	},
	totalsLabel: {
		fontSize,
		lineHeight,
		minWidth: 0,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		textTransform: "uppercase",
		color: theme.palette.text.primary
	},
	totalsPrice: {
		fontSize,
		lineHeight,
		color: theme.palette.text.primary
	},
	totalsTotalItem: {
		margin: 0,
		padding: `${theme.spacing(2)}px ${theme.spacing(1)}px 0 ${theme.spacing(1)}px`,
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between"
	},
	totalsTotalLabel: {
		fontSize,
		lineHeight,
		fontWeight: 500,
		textTransform: "uppercase"
	},
	totalsTotalPrice: {
		fontSize,
		lineHeight,
		fontWeight: 500
	},
	summary: {
		marginTop: theme.spacing(1),
		listStyle: "none",
		borderTop: "1px solid #bdbdbd"
	},
	summaryItem: {
		margin: 0,
		padding: `${theme.spacing(1)}px ${theme.spacing(1)}px 0 ${theme.spacing(1)}px`,
		display: "flex",
		flexDirection: "row",
		alignItems: "baseline",
		justifyContent: "space-between"
	},
	summaryLabel: {
		fontSize,
		lineHeight,
		minWidth: 0,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		textTransform: "uppercase"
	},
	summaryValue: {
		fontSize,
		lineHeight,
		minWidth: 0,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		textTransform: "uppercase",
		textAlign: "right"
	},
	copyInfo: {
		marginTop: "32px",
		textTransform: "uppercase",
		textAlign: "center"
	}
}));

const ReceiptScreen = () => {
	const { formatAddressShort } = useFormatAddress();
	const { t } = useTranslation();

	const query = parse(useLocation()?.search?.substr(1) || "");
	const tempAccessToken = query.t ? (Array.isArray(query.t) ? query.t[0] : query.t) : null;
	const { orderId } = useParams();

	const { loading, data } = useQuery(OrderQuery, {
		variables: {
			orderInput: {
				orderId,
				refreshStatus: true,
				tempAccessToken
			}
		},
		fetchPolicy: "network-only"
	});

	const classes = useStyles();

	// TODO: separate business context?

	if (loading) {
		return <Loading />;
	}

	if (!data || !data.order) {
		return <h3>Order #{orderId} not found</h3>;
	}

	const { order } = data;
	const { orderType, orderItems, deliveryAddress, customer, user } = order;

	const orderedAddress = deliveryAddress && formatAddressShort(deliveryAddress as Address);

	return (
		<div className={classes.root}>
			<BusinessHeader isReceiptMode />

			<Box className={classes.orderInfo}>
				<Typography className={classes.subheader}>
					{t("orderNumber")}: {order!.orderNumber}
				</Typography>
				<Typography className={classes.orderInfoPersonalInformationHeader}>
					<UserIcon /> {t("customerInformation")}
				</Typography>
				<Typography className={classes.orderInfoDeliveryInfo}>
					{customer?.fullName}
					<br />
					{order?.displayPhoneNumber}
					<br />
					{orderedAddress && (
						<>
							{orderedAddress}
							<br />
						</>
					)}
					{user.email}
				</Typography>
			</Box>

			<Typography className={classes.itemsHeader}>
				<ReceiptIcon fill="4F4F4F" /> {t("yourReceipt")}
			</Typography>

			<List className={classes.items}>
				{orderItems.map(orderItem => (
					<OrderItemListItem key={orderItem.id} className={classes.item} item={orderItem} variant="light" />
				))}
			</List>

			<List className={classes.totals}>
				<ListItem className={classes.totalsItem}>
					<Typography className={classes.totalsLabel}>{t("foodBeverageTotal")}</Typography>
					<Price className={classes.totalsPrice} priceCents={order!.orderSubTotalCents} />
				</ListItem>

				{!!order?.discount && (
					<ListItem className={classes.totalsItem}>
						<Typography className={classes.totalsLabel}>{t("discount")}</Typography>
						<Price className={classes.totalsPrice} priceCents={order.discount} prefix="-" />
					</ListItem>
				)}

				{orderType?.orderType !== AvailableOrderTypes.delivery ? null : (
					<ListItem className={classes.totalsItem}>
						<Typography className={classes.totalsLabel}>{t("deliveryFee")}</Typography>
						<Price className={classes.totalsPrice} priceCents={orderType.priceCents || 0} />
					</ListItem>
				)}

				{
					// <ListItem className={classes.totalsItem}>
					// 	<Typography className={classes.totalsLabel}>{t("courierTip")}</Typography>
					// 	<Price className={classes.totalsPrice} priceCents={order!.tipCents} />
					// </ListItem>
				}

				<ListItem className={classes.totalsItem}>
					<Typography className={classes.totalsLabel}>{t("tax", { taxRate: order!.taxRate })}</Typography>
					<Price className={classes.totalsPrice} priceCents={order!.taxCents} />
				</ListItem>

				<ListItem className={classes.totalsItem}>
					<Typography className={classes.totalsLabel}>{t("tip")}</Typography>
					<Price className={classes.totalsPrice} priceCents={order!.tipCents} />
				</ListItem>

				<ListItem className={classes.totalsTotalItem}>
					<Typography className={classes.totalsTotalLabel}>{t("total")}</Typography>
					<Price className={classes.totalsTotalPrice} priceCents={order!.orderTotalCents} />
				</ListItem>
			</List>

			<List className={classes.summary}>
				<ListItem className={classes.summaryItem}>
					<Typography className={classes.summaryLabel}>{t("paidWith")}</Typography>
					{order?.payment && (
						<Typography className={classes.summaryValue}>
							{order?.payment?.card?.brand || "card"} ending {order!.payment!.card!.last4}
						</Typography>
					)}
					{!order?.payment && <Typography className={classes.summaryValue}>{t("notPaid")}</Typography>}
				</ListItem>

				<ListItem className={classes.summaryItem}>
					<Typography className={classes.summaryLabel}>{t("amount")}</Typography>
					<Price className={classes.summaryValue} priceCents={order!.orderTotalCents} />
				</ListItem>

				<ListItem className={classes.summaryItem}>
					<Typography className={classes.summaryLabel}>{t("transactionType")}</Typography>
					<Typography className={classes.summaryValue}>{t("purchase")}</Typography>
				</ListItem>

				<ListItem className={classes.summaryItem}>
					<Typography className={classes.summaryLabel}>{t("date")}</Typography>
					<Typography className={classes.summaryValue}>
						{DateTime.fromJSDate(new Date()).toFormat("MMM dd, yyyy")}
						<br />
						{DateTime.fromJSDate(new Date()).toFormat("h:mm a")}
					</Typography>
				</ListItem>
			</List>
			{/* <Box>
				<Typography className={classes.copyInfo}>
					{t("copySentTo")}
					<br />
					{user.email || order?.displayPhoneNumber}
				</Typography>
			</Box> */}
		</div>
	);
};

export default ReceiptScreen;
